<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import EvolutionInstancesAPI from 'dashboard/api/evolutionInstances';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const QR_POLL_INTERVAL = 3000; // 3 seconds
const QR_EXPIRY_SECONDS = 60;

export default {
  components: {
    PageHeader,
    NextButton,
    Spinner,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      // Form state
      inboxName: '',
      alwaysOnline: true,
      readMessages: true,
      readStatus: false,
      humanTakeoverPausesBot: true,

      // Creation flow state
      step: 'form', // 'form' | 'qr_code' | 'connected'
      isCreating: false,

      // Pending instance (new flow)
      pendingInstanceId: null,
      instanceName: null,
      createdInboxId: null,

      // QR Code state
      qrCode: null,
      qrTimeRemaining: QR_EXPIRY_SECONDS,
      qrPollingInterval: null,
      qrCountdownInterval: null,
      isRefreshingQr: false,

      // Status
      connectionStatus: 'pending',
      error: null,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      uiFlags: 'inboxes/getUIFlags',
    }),
    showForm() {
      return this.step === 'form';
    },
    showQrCode() {
      return this.step === 'qr_code';
    },
    showConnected() {
      return this.step === 'connected';
    },
    qrCodeSrc() {
      if (!this.qrCode) return '';
      // Evolution API may already return with data:image prefix
      if (this.qrCode.startsWith('data:image')) {
        return this.qrCode;
      }
      return `data:image/png;base64,${this.qrCode}`;
    },
  },
  validations: {
    inboxName: { required },
  },
  mounted() {
    // Listen for ActionCable events
    emitter.on(
      BUS_EVENTS.EVOLUTION_STATUS_CHANGED,
      this.onEvolutionStatusChanged
    );
  },
  beforeUnmount() {
    this.stopPolling();
    this.cleanupPendingInstance();
    emitter.off(
      BUS_EVENTS.EVOLUTION_STATUS_CHANGED,
      this.onEvolutionStatusChanged
    );
  },
  methods: {
    onEvolutionStatusChanged(data) {
      // Check if this event is for our pending instance
      if (
        data.pending_instance_id !== this.pendingInstanceId &&
        data.inbox_id !== this.createdInboxId
      ) {
        return;
      }

      if (data.status === 'connected') {
        // Store the created inbox ID from the webhook response
        if (data.inbox_id) {
          this.createdInboxId = data.inbox_id;
        }
        this.handleConnected();
      }
    },

    async createInstance() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      this.isCreating = true;
      this.error = null;

      try {
        // Create Evolution instance FIRST (without creating inbox)
        const { data } = await EvolutionInstancesAPI.create({
          inbox_name: this.inboxName,
          always_online: this.alwaysOnline,
          read_messages: this.readMessages,
          read_status: this.readStatus,
          human_takeover_pauses_bot: this.humanTakeoverPausesBot,
        });

        if (data.success) {
          this.pendingInstanceId = data.pending_instance_id;
          this.instanceName = data.instance_name;
          this.qrCode = data.qrcode;
          this.step = 'qr_code';
          this.startPolling();
        } else {
          throw new Error(data.error || 'Failed to create Evolution instance');
        }
      } catch (error) {
        this.error =
          error.response?.data?.error ||
          error.message ||
          this.$t('INBOX_MGMT.ADD.VIPER_CONNECT.ERROR_MESSAGE');
        useAlert(this.error);
      } finally {
        this.isCreating = false;
      }
    },

    async refreshQrCode() {
      if (!this.pendingInstanceId || this.isRefreshingQr) return;

      this.isRefreshingQr = true;
      this.error = null;

      try {
        const { data } = await EvolutionInstancesAPI.getQrCode(
          this.pendingInstanceId
        );
        this.qrCode = data.qrcode;
        this.qrTimeRemaining = QR_EXPIRY_SECONDS;

        // Check if already connected
        if (
          data.pending_status === 'connected' ||
          data.status === 'open' ||
          data.status === 'connected'
        ) {
          await this.checkStatusAndHandleConnected();
        }
      } catch (error) {
        if (error.response?.status === 410) {
          // Instance expired
          this.error = this.$t('INBOX_MGMT.ADD.VIPER_CONNECT.EXPIRED');
          this.step = 'form';
          this.resetState();
        } else {
          this.error =
            error.response?.data?.error || 'Failed to refresh QR code';
        }
      } finally {
        this.isRefreshingQr = false;
      }
    },

    async checkStatus() {
      if (!this.pendingInstanceId) return;

      try {
        const { data } = await EvolutionInstancesAPI.getStatus(
          this.pendingInstanceId
        );

        if (data.status === 'connected') {
          if (data.inbox_id) {
            this.createdInboxId = data.inbox_id;
          }
          await this.handleConnected();
        } else if (data.status === 'expired') {
          this.error = this.$t('INBOX_MGMT.ADD.VIPER_CONNECT.EXPIRED');
          this.step = 'form';
          this.resetState();
        }
      } catch (error) {
        if (error.response?.status === 410) {
          this.error = this.$t('INBOX_MGMT.ADD.VIPER_CONNECT.EXPIRED');
          this.step = 'form';
          this.resetState();
        }
        // Ignore other polling errors
      }
    },

    async checkStatusAndHandleConnected() {
      try {
        const { data } = await EvolutionInstancesAPI.getStatus(
          this.pendingInstanceId
        );
        if (data.status === 'connected' && data.inbox_id) {
          this.createdInboxId = data.inbox_id;
          await this.handleConnected();
        }
      } catch (error) {
        // Ignore errors
      }
    },

    async handleConnected() {
      this.stopPolling();
      this.step = 'connected';

      // Wait for inbox_id if not yet available
      if (!this.createdInboxId) {
        // Poll for inbox_id
        for (let i = 0; i < 10; i++) {
          try {
            const { data } = await EvolutionInstancesAPI.getStatus(
              this.pendingInstanceId
            );
            if (data.inbox_id) {
              this.createdInboxId = data.inbox_id;
              break;
            }
          } catch (error) {
            // Continue polling
          }
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // Reload inboxes to get the new one
      await this.$store.dispatch('inboxes/get');

      // Small delay to show success message
      setTimeout(() => {
        if (this.createdInboxId) {
          router.replace({
            name: 'settings_inboxes_add_agents',
            params: {
              page: 'new',
              inbox_id: this.createdInboxId,
            },
          });
        } else {
          // Fallback: go to inbox list
          router.replace({ name: 'settings_inbox_list' });
        }
      }, 1500);
    },

    startPolling() {
      this.stopPolling();

      // Poll for connection status
      this.qrPollingInterval = setInterval(async () => {
        await this.checkStatus();
      }, QR_POLL_INTERVAL);

      // Countdown timer
      this.qrCountdownInterval = setInterval(() => {
        this.qrTimeRemaining -= 1;

        if (this.qrTimeRemaining <= 0) {
          this.refreshQrCode();
        }
      }, 1000);
    },

    stopPolling() {
      if (this.qrPollingInterval) {
        clearInterval(this.qrPollingInterval);
        this.qrPollingInterval = null;
      }
      if (this.qrCountdownInterval) {
        clearInterval(this.qrCountdownInterval);
        this.qrCountdownInterval = null;
      }
    },

    resetState() {
      this.pendingInstanceId = null;
      this.instanceName = null;
      this.createdInboxId = null;
      this.qrCode = null;
      this.qrTimeRemaining = QR_EXPIRY_SECONDS;
    },

    async cleanupPendingInstance() {
      // If user navigates away without connecting, cleanup the pending instance
      if (this.pendingInstanceId && this.step === 'qr_code') {
        try {
          await EvolutionInstancesAPI.destroy(this.pendingInstanceId);
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    },
  },
};
</script>

<template>
  <div class="w-full">
    <!-- Step 1: Form -->
    <div v-if="showForm">
      <PageHeader
        :header-title="$t('INBOX_MGMT.ADD.VIPER_CONNECT.TITLE')"
        :header-content="$t('INBOX_MGMT.ADD.VIPER_CONNECT.DESC')"
      />

      <!-- Error message -->
      <div
        v-if="error"
        class="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
      >
        <p class="text-red-700 dark:text-red-400 text-sm">{{ error }}</p>
      </div>

      <form
        class="flex flex-wrap flex-col mx-0"
        @submit.prevent="createInstance"
      >
        <!-- Inbox Name -->
        <div class="flex-shrink-0 flex-grow-0 mb-4">
          <label :class="{ error: v$.inboxName.$error }">
            {{ $t('INBOX_MGMT.ADD.VIPER_CONNECT.INBOX_NAME.LABEL') }}
            <input
              v-model="inboxName"
              type="text"
              :placeholder="
                $t('INBOX_MGMT.ADD.VIPER_CONNECT.INBOX_NAME.PLACEHOLDER')
              "
              @blur="v$.inboxName.$touch"
            />
            <span v-if="v$.inboxName.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.VIPER_CONNECT.INBOX_NAME.ERROR') }}
            </span>
          </label>
        </div>

        <!-- Settings -->
        <div class="flex-shrink-0 flex-grow-0 mb-6">
          <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
            {{ $t('INBOX_MGMT.ADD.VIPER_CONNECT.SETTINGS.TITLE') }}
          </h4>

          <div class="space-y-3">
            <label class="flex items-center gap-3 cursor-pointer">
              <input
                v-model="alwaysOnline"
                type="checkbox"
                class="w-4 h-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500"
              />
              <div>
                <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {{ $t('INBOX_MGMT.ADD.VIPER_CONNECT.SETTINGS.ALWAYS_ONLINE') }}
                </span>
                <p class="text-xs text-slate-500">
                  {{
                    $t(
                      'INBOX_MGMT.ADD.VIPER_CONNECT.SETTINGS.ALWAYS_ONLINE_DESC'
                    )
                  }}
                </p>
              </div>
            </label>

            <label class="flex items-center gap-3 cursor-pointer">
              <input
                v-model="readMessages"
                type="checkbox"
                class="w-4 h-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500"
              />
              <div>
                <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {{
                    $t('INBOX_MGMT.ADD.VIPER_CONNECT.SETTINGS.READ_MESSAGES')
                  }}
                </span>
                <p class="text-xs text-slate-500">
                  {{
                    $t(
                      'INBOX_MGMT.ADD.VIPER_CONNECT.SETTINGS.READ_MESSAGES_DESC'
                    )
                  }}
                </p>
              </div>
            </label>

            <label class="flex items-center gap-3 cursor-pointer">
              <input
                v-model="readStatus"
                type="checkbox"
                class="w-4 h-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500"
              />
              <div>
                <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {{ $t('INBOX_MGMT.ADD.VIPER_CONNECT.SETTINGS.READ_STATUS') }}
                </span>
                <p class="text-xs text-slate-500">
                  {{
                    $t('INBOX_MGMT.ADD.VIPER_CONNECT.SETTINGS.READ_STATUS_DESC')
                  }}
                </p>
              </div>
            </label>

            <!-- Human Takeover Setting -->
            <div class="pt-3 mt-3 border-t border-slate-200 dark:border-slate-600">
              <label class="flex items-center gap-3 cursor-pointer">
                <input
                  v-model="humanTakeoverPausesBot"
                  type="checkbox"
                  class="w-4 h-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500"
                />
                <div>
                  <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {{
                      $t(
                        'INBOX_MGMT.ADD.VIPER_CONNECT.SETTINGS.HUMAN_TAKEOVER'
                      )
                    }}
                  </span>
                  <p class="text-xs text-slate-500">
                    {{
                      $t(
                        'INBOX_MGMT.ADD.VIPER_CONNECT.SETTINGS.HUMAN_TAKEOVER_DESC'
                      )
                    }}
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div class="w-full">
          <NextButton
            :is-loading="isCreating"
            type="submit"
            :label="$t('INBOX_MGMT.ADD.VIPER_CONNECT.SUBMIT_BUTTON')"
          />
        </div>
      </form>
    </div>

    <!-- Step 2: QR Code -->
    <div v-else-if="showQrCode" class="text-center">
      <div class="mb-6">
        <h2 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-2">
          {{ $t('INBOX_MGMT.ADD.VIPER_CONNECT.QR.TITLE') }}
        </h2>
        <p class="text-sm text-slate-500 dark:text-slate-400">
          {{ $t('INBOX_MGMT.ADD.VIPER_CONNECT.QR.DESC') }}
        </p>
      </div>

      <!-- QR Code Image -->
      <div
        class="bg-white dark:bg-slate-700 rounded-xl p-4 mb-4 inline-block shadow-sm"
      >
        <img
          v-if="qrCode"
          :src="qrCodeSrc"
          alt="QR Code"
          class="w-64 h-64"
        />
        <div v-else class="w-64 h-64 flex items-center justify-center">
          <Spinner size="48" />
        </div>
      </div>

      <!-- Timer -->
      <div class="flex items-center justify-center gap-2 mb-4 text-sm">
        <span class="text-slate-500 dark:text-slate-400">
          {{ $t('INBOX_MGMT.ADD.VIPER_CONNECT.QR.EXPIRES_IN') }}:
          <span
            :class="{
              'text-red-500': qrTimeRemaining <= 15,
              'text-yellow-500':
                qrTimeRemaining > 15 && qrTimeRemaining <= 30,
              'text-slate-700 dark:text-slate-300': qrTimeRemaining > 30,
            }"
          >
            {{ qrTimeRemaining }}s
          </span>
        </span>
      </div>

      <NextButton
        :label="$t('INBOX_MGMT.ADD.VIPER_CONNECT.QR.REFRESH')"
        :is-loading="isRefreshingQr"
        variant="smooth"
        color="slate"
        icon="i-lucide-refresh-cw"
        @click="refreshQrCode"
      />
    </div>

    <!-- Step 3: Connected -->
    <div v-else-if="showConnected" class="text-center py-12">
      <div
        class="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center"
      >
        <svg
          class="w-8 h-8 text-green-600 dark:text-green-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <h2 class="text-lg font-medium text-green-800 dark:text-green-300 mb-2">
        {{ $t('INBOX_MGMT.ADD.VIPER_CONNECT.CONNECTED.TITLE') }}
      </h2>
      <p class="text-sm text-slate-500 dark:text-slate-400">
        {{ $t('INBOX_MGMT.ADD.VIPER_CONNECT.CONNECTED.DESC') }}
      </p>
    </div>
  </div>
</template>
